import { useEffect, useState } from "react";
import { ChevronDown, ListVideo, Play } from "lucide-react";
import { mediaSrc } from "@/components/admin/ImageUpload";

export const TrainingSeries = ({ series }) => {
  const [openId, setOpenId] = useState(null);
  const [playing, setPlaying] = useState({});

  useEffect(() => {
    setOpenId((cur) => cur ?? series[0]?.id ?? null);
  }, [series]);

  if (series.length === 0) {
    return (
      <div
        data-testid="no-training"
        className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-8 text-center text-sm text-slate-500"
      >
        No training series published yet — new series appear here automatically.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {series.map((s) => {
        const open = openId === s.id;
        const current = playing[s.id] || s.videos[0];
        return (
          <div
            key={s.id}
            data-testid={`training-series-${s.id}`}
            className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setOpenId(open ? null : s.id)}
              data-testid={`series-toggle-${s.id}`}
              className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
            >
              <div className="h-16 w-24 sm:h-20 sm:w-32 shrink-0 rounded-xl overflow-hidden bg-slate-100 dark:bg-white/5 grid place-items-center">
                {s.cover ? (
                  <img src={mediaSrc(s.cover)} alt={s.title} className="h-full w-full object-cover" />
                ) : (
                  <ListVideo className="h-5 w-5 text-slate-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-base md:text-lg font-semibold tracking-tight truncate">
                  {s.title}
                </h3>
                <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{s.description}</p>
                <p className="mt-1 text-[11px] text-[#06B6D4] font-semibold">{s.video_count} videos</p>
              </div>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
              />
            </button>

            {open && (
              <div className="border-t border-slate-100 dark:border-white/5 p-4 space-y-4">
                {current && (
                  <div>
                    <div className="aspect-video bg-black rounded-xl overflow-hidden">
                      <iframe
                        src={current.embed_url}
                        title={current.title}
                        allow="autoplay; fullscreen; encrypted-media"
                        allowFullScreen
                        className="h-full w-full"
                        data-testid={`series-player-${s.id}`}
                      />
                    </div>
                    <div className="mt-2.5">
                      <div className="text-sm font-semibold">{current.title}</div>
                      {current.description && (
                        <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">{current.description}</p>
                      )}
                    </div>
                  </div>
                )}

                <ul className="divide-y divide-slate-100 dark:divide-white/5 rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
                  {s.videos.map((v) => {
                    const active = current?.id === v.id;
                    return (
                      <li key={v.id}>
                        <button
                          type="button"
                          onClick={() => setPlaying({ ...playing, [s.id]: v })}
                          data-testid={`series-video-item-${v.id}`}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            active ? "bg-[#06B6D4]/10" : "hover:bg-slate-50 dark:hover:bg-white/5"
                          }`}
                        >
                          <span className="h-7 w-7 shrink-0 grid place-items-center rounded-lg bg-slate-100 dark:bg-white/5">
                            <Play className={`h-3 w-3 ${active ? "text-[#06B6D4]" : "text-slate-400"}`} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium truncate">
                              Video {v.order}: {v.title}
                            </span>
                            {v.description && (
                              <span className="block text-[11px] text-slate-500 truncate">{v.description}</span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
